import { useCallback, useEffect, useRef, useState } from 'react';

import { generateAppealLetter } from '../../api/claimsClient';
import LetterSectionsPanel from '../letterSections/LetterSectionsPanel';

/**
 * Line-item appeal letter surface (LTR-08). Thin shell mounting the shared
 * structured-letter panel (Plan 06-08). The legacy click-to-edit markdown
 * block model and the whole-letter blob PATCH are gone — editing, autosave,
 * approval, preview and download now live inside LetterSectionsPanel;
 * per-section regenerate rides the existing appeal generate endpoint with
 * section_keys=[key].
 *
 * CLAUDE.md effect safety: sectionsRefreshKey is bumped exactly once when the
 * host's generationStatus transitions INTO a terminal success (tracked via a
 * previous-status ref) — never on a callback-identity change and never twice
 * for the same terminal status.
 *
 * Props:
 *   appealId          — string (the Appeal UUID; letterId for letterType="appeal")
 *   claimId           — string (needed by the appeal generate endpoint)
 *   templateId        — string|null (resolved by the parent from denial category)
 *   generationStatus  — 'generating' | 'generated' | undefined
 *   onGenerationStart — () => void; parent flips status/starts polling after a regenerate
 *   onAlert           — (message: string) => void
 */
export default function LetterEditor({
  appealId,
  claimId,
  templateId,
  generationStatus,
  onGenerationStart,
  onAlert,
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const prevStatusRef = useRef(generationStatus);
  const onGenerationStartRef = useRef(onGenerationStart);
  const onAlertRef = useRef(onAlert);
  onGenerationStartRef.current = onGenerationStart;
  onAlertRef.current = onAlert;

  // Bump the panel's refresh key once on the generating -> generated edge so a
  // terminal generation fetches fresh sections exactly once (no callback loop).
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = generationStatus;
    if (prev === 'generating' && generationStatus === 'generated') {
      setRefreshKey((k) => k + 1);
    }
  }, [generationStatus]);

  const handleRegenerateSection = useCallback(
    async (sectionKey) => {
      if (!claimId || !templateId) {
        onAlertRef.current?.("Couldn't regenerate this section. Try again.");
        return;
      }
      try {
        await generateAppealLetter({ claimId, templateId, section_keys: [sectionKey] });
        onGenerationStartRef.current?.();
      } catch (err) {
        onAlertRef.current?.("Couldn't regenerate this section. Try again.");
      }
    },
    [claimId, templateId]
  );

  if (!appealId) return null;

  return (
    <LetterSectionsPanel
      letterType="appeal"
      letterId={appealId}
      sectionsRefreshKey={refreshKey}
      generationStatus={generationStatus}
      onRegenerateSection={handleRegenerateSection}
      onAlert={onAlert}
    />
  );
}
